import {
  DashboardBuilderChartWidget,
  DashboardBuilderWidget,
  getColSpanForViewType,
  widgetOperations,
  widgetTypes,
} from '@ardoq/dashboard';
import { isEmpty } from 'lodash';
import {
  Aggregate,
  APIBarChartAttributes,
  APIFieldType,
  APIPieChartAttributes,
  ChartOrderOptions,
  DataSourceField,
  WidgetErrorCode,
  WidgetTypes,
} from '@ardoq/api-types';
import { getNewNumberWidgetTrend, getSelectedFields } from '../utils';
import { getValidAggregates } from '../aggregateValidation';
import { isWidgetTypeValidForSelectedFieldsAndDatasource } from '../widgetTypeValidation';
import { DashboardLevelValidationErrors } from '../types';
import {
  DashboardFilterPillUIState,
  DashboardFilterPillsUIStateWithSetters,
  getInitialDashboardFilterPillUIState,
} from '@ardoq/table-filter-bar';
import { dispatchAction } from '@ardoq/rxbeach';
import {
  dashboardFilterDatePickerToggled,
  dashboardFilterPillInputValueChanged,
  dashboardFilterPillOperatorPopoverToggled,
  dashboardFilterPillValuePopoverToggled,
} from 'dashboard/DashboardBuilder/streams/actions';

type WidgetsToUpdate = Array<
  Partial<DashboardBuilderWidget> &
    Required<Pick<DashboardBuilderWidget, '_id'>>
>;
export const getUpdatedWidgets = (
  widgets: DashboardBuilderWidget[],
  widgetsToUpdate: WidgetsToUpdate
): Array<DashboardBuilderWidget> =>
  widgets.map(widget => {
    const widgetToUpdate = widgetsToUpdate.find(
      item => item._id === widget._id
    );

    if (!widgetToUpdate) return widget;

    if (widgetOperations.isHeaderWidgetData(widget)) {
      return { ...widget, ...widgetToUpdate } as typeof widget;
    }

    if (widgetOperations.isNoteWidgetData(widget)) {
      const updated = { ...widget, ...widgetToUpdate };
      return {
        ...updated,
        validationProblems: !isEmpty(updated.content)
          ? null
          : updated.validationProblems,
      } as typeof widget;
    }

    if (widgetOperations.isPowerBiIframeWidgetData(widget)) {
      return { ...widget, ...widgetToUpdate } as typeof widget;
    }

    const updatedChartAttributes =
      widgetToUpdate as Partial<DashboardBuilderChartWidget>;

    const { error, errorCode, traceId, ...widgetAttributes } = widget;

    const backendError =
      updatedChartAttributes.datasource &&
      errorCode === WidgetErrorCode.DELETED_DATASOURCE
        ? {}
        : { error, errorCode, traceId };

    return {
      ...widgetAttributes,
      ...backendError,
      ...updatedChartAttributes,
    } as DashboardBuilderChartWidget;
  });

export const hasValidationProblems = (
  validationErrors: DashboardLevelValidationErrors,
  widgets: DashboardBuilderWidget[]
) =>
  // Reset showing validation problems as errors if the only problem has been fixed now
  !isEmpty(validationErrors) ||
  widgets.some(({ validationProblems }) => !isEmpty(validationProblems));

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export const getWidgetTypeLayoutFieldAggregateAndSort = (
  selectedWidget: DashboardBuilderChartWidget,
  availableFieldsForDataSource: DataSourceField[],
  shouldResetWidgetSize?: boolean
) => {
  // When the selected data source, field or widget type changes, some other properties might have to change as well.
  // This function should make sure those properties that depend on each other are changed consistently
  const selectedFields = getSelectedFields(
    selectedWidget,
    availableFieldsForDataSource
  );

  const validWidgetTypes = widgetTypes.filter(widgetType =>
    isWidgetTypeValidForSelectedFieldsAndDatasource({
      widgetType,
      datasource: selectedWidget.datasource,
      availableFieldsForDataSource: availableFieldsForDataSource,
      selectedFields,
    })
  );

  const widgetType = validWidgetTypes.some(
    widgetType => widgetType === selectedWidget.viewType
  )
    ? selectedWidget.viewType
    : validWidgetTypes[0];

  const aggregate = changeAggregateIfNotValid({
    widgetType,
    fieldTypes: selectedFields.map(({ type }) => type),
    selectedAggregate: selectedWidget.aggregate,
  });

  const orderSelection = widgetOperations.isOrderSelectionValid(
    (selectedWidget as APIBarChartAttributes | APIPieChartAttributes)
      .orderSelection,
    selectedFields[0] && selectedFields[0].type
  )
    ? (selectedWidget as APIBarChartAttributes | APIPieChartAttributes)
        .orderSelection
    : ChartOrderOptions.SIZE;

  // dimensions should reset only when changing widget type (programmatically or manually)
  const shouldResetDimensions =
    shouldResetWidgetSize || widgetType !== selectedWidget.viewType;

  const layout = {
    ...selectedWidget.layout,
    w: shouldResetDimensions
      ? getColSpanForViewType(widgetType)
      : selectedWidget.layout.w,
    h: shouldResetDimensions ? 1 : selectedWidget.layout.h,
  };

  const newTrend = widgetOperations.isNumberWidgetData(selectedWidget)
    ? getNewNumberWidgetTrend(selectedWidget)
    : undefined;

  let orderedSelectedFields = selectedFields;
  if (
    widgetOperations.isCategoricalChartType(widgetType) &&
    selectedFields.length === 2
  ) {
    if (
      selectedFields[0].type === APIFieldType.NUMBER &&
      selectedFields[1].type !== APIFieldType.NUMBER
    ) {
      // Categorical charts require that label field first and the Number field second
      orderedSelectedFields = selectedFields.toReversed();
    }
  }

  const fields = orderedSelectedFields.map(({ name }) => name);

  return {
    layout,
    viewType: widgetType!,
    aggregate,
    orderSelection,
    filters: selectedWidget.filters,
    fields,
    ...(widgetOperations.isTableWidgetData(selectedWidget)
      ? { sort: selectedWidget.sort, order: selectedWidget.order }
      : {}),
    trend: newTrend,
  };
};

const changeAggregateIfNotValid = ({
  widgetType,
  fieldTypes,
  selectedAggregate,
}: {
  widgetType?: WidgetTypes;
  fieldTypes: APIFieldType[];
  selectedAggregate: Aggregate | undefined;
}) => {
  const validAggregates = getValidAggregates({
    fieldTypes,
    widgetType,
  });
  // If there are no valid aggregates, we deselect the aggregate.
  // If there is only one valid aggregate, except Table widget, we preselect that one. Else use the user selected aggregate
  return !validAggregates.length
    ? undefined
    : validAggregates.some(aggregate => aggregate === selectedAggregate)
      ? selectedAggregate
      : widgetType === WidgetTypes.TABLE
        ? undefined
        : validAggregates[0];
};

export const setDefaultAggregateOnCategoricalCharts = (
  widget: DashboardBuilderWidget
): DashboardBuilderWidget => {
  if (widgetOperations.isCategoricalChartData(widget) && !widget.aggregate) {
    // TODO ARD-27405  drop this tmp code after persisted widgets migrated to always have an aggregate
    widget.aggregate =
      widget.fields.length === 2 ? Aggregate.SUM : Aggregate.COUNT;
  }
  return widget;
};

export const toDashboardFilterPillsUIStateWithSetters = (
  filterId: string,
  filterPillUI$State: Record<string, DashboardFilterPillUIState>
): DashboardFilterPillsUIStateWithSetters => {
  const filterPillUIState =
    filterPillUI$State[filterId] ?? getInitialDashboardFilterPillUIState();

  return {
    ...filterPillUIState,
    toggleIsOperatorPopoverOpen: () => {
      dispatchAction(
        dashboardFilterPillOperatorPopoverToggled({
          dashboardFilterId: filterId,
          isOpen: !filterPillUIState.isOperatorPopoverOpen, // flip the flag here to toggle it
        })
      );
    },
    toggleIsValuePopoverOpen: () =>
      dispatchAction(
        dashboardFilterPillValuePopoverToggled({
          dashboardFilterId: filterId,
          isOpen: !filterPillUIState.isValuePopoverOpen,
        })
      ),
    toggleShowDatePicker: () =>
      dispatchAction(
        dashboardFilterDatePickerToggled({
          dashboardFilterId: filterId,
          isOpen: !filterPillUIState.showDatePicker,
        })
      ),
    setPopoverInputValue: (inputValue: string | number | null) =>
      dispatchAction(
        dashboardFilterPillInputValueChanged({
          dashboardFilterId: filterId,
          inputValue,
        })
      ),
  };
};
